/*
 * File: c:\Users\j.mazat\Desktop\AutobuyBackOffice\AutobuyBackOffice\src\Services\Car\CarService.ts
 * Project: c:\Users\j.mazat\Desktop\AutobuyBackOffice\AutobuyBackOffice
 * Created Date: Thursday June 8th 2023
 * Author: Jonathan Mazat
 * -----
 * Last Modified: Monday, 3rd June 2024 10:46:09
 * Modified By: Simon (s.gourlet@autobuy.fr>)
 * -----
 * Copyright (c) 2023 Autobuy SAS
 * -----
 */

import Config from "Config/Config";
import { TemplateCarDataType } from "Type/Car.type";
import { EquipmentsToUpdate, SingleCarFormat, UpdateCarType } from "Type/CarElement.type.hold";
import generalQueryParams from "Type/params.type";
import { PriceListRefType } from "Type/pricing.type";
import { buildAxiosHeader, buildAxiosHeaderFile, buildURL } from "Utils/Request.utils";
import Axios from "axios";

export default class CarService {
    static async transformSingleCar(car: any): Promise<SingleCarFormat | null> {
        if (car === undefined || car === null)
            return null;
        const newCar = new SingleCarFormat();
        newCar.fromJson(car);
        return newCar;
    }

    static async getCarByID(id: string) {
        const url = `${Config.BACKEND.BASEURL}${Config.BACKEND.PATHS.CARS}/${id}`;
        const myCar = await Axios.get(url, buildAxiosHeader());
        return myCar.data;
    }

    static async getCarByIDForSearch(id: string) {
        const url = `${Config.BACKEND.BASEURL}${Config.BACKEND.PATHS.CARS}/${id}/for_search`;
        const myCar = await Axios.get(url, buildAxiosHeader());
        return myCar.data;
    }

    static async getCarOffersByID(id: string) {
        const url = `${Config.BACKEND.BASEURL}${Config.BACKEND.PATHS.CARS}/${id}/offers`;
        const myCarOffers = await Axios.get(url, buildAxiosHeader());
        return (myCarOffers.data.InSell as any[]).map(v => v.Offers).flat(2);
    }

    static async getCarOldOffersByID(id: string) {
        const url = `${Config.BACKEND.BASEURL}${Config.BACKEND.PATHS.CARS}/${id}/offers`;
        const myCarOffers = await Axios.get(url, buildAxiosHeader());
        return myCarOffers.data.InSell;
    }

    static async getSendCarIntelToClient(carID: string, userID: string) {
        const url = `${Config.BACKEND.BASEURL}${Config.BACKEND.PATHS.CARS}/${carID}/send_intels`;
        return Axios.post(url, {userID}, buildAxiosHeader());
    }

    static async setCarByID(id: string, data: UpdateCarType) {
        const url = `${Config.BACKEND.BASEURL}${Config.BACKEND.PATHS.CARS}/${id}`;
        try {
            return await Axios.put(url, data, buildAxiosHeader());
        } catch (e) {
            console.error(`Not able to update car: ${JSON.stringify(e)}`);
        }
        return null;
    }

    static async setCarPicturesByID(id: string, pictures_update: any) {
        const url = `${Config.BACKEND.BASEURL}${Config.BACKEND.PATHS.CARS}/${id}/car_pictures`;
        const body = pictures_update;
        try {
            return await Axios.put(url, body, buildAxiosHeader());
        } catch (e) {
            console.error(`Not able to update car pictures: ${JSON.stringify(e)}`);
        }
        return null;
    }

    static async deleteCarFromSell(sellId: string, carId: string) {
        const url = `${Config.BACKEND.BASEURL}${Config.BACKEND.PATHS.SELLS}/${sellId}/${carId}`;
        const headers = buildAxiosHeader();
        try {
            return await Axios.put(url, {}, headers);
        } catch (e) {
            console.error(`Not able to update car: ${JSON.stringify(e)}`);
            throw e;
        }
    }

    static createCarPicture(id: string, name: string, index: number, picture: File) {
        const url = `${Config.BACKEND.BASEURL}${Config.BACKEND.PATHS.CARS}/${id}/add_picture`;
        var body = {picture, name, index};
        return Axios.put(url, body, buildAxiosHeaderFile());
    }

    static async getCarsByRegNb(regNb: string) {
        const url = `${Config.BACKEND.BASEURL}${Config.BACKEND.PATHS.CARS}/regnb/${regNb}`;
        const headers = buildAxiosHeader();
        try {
            const cars = await Axios.get<any[]>(url, headers);
            return cars.data;
        } catch (e) {
            console.error(`Not able to pull cars with regNb containing ${regNb}: ${JSON.stringify(e)}`);
        }
        return [];
    }

    static async makeOfferForCar(carID: string, offerValue: number, myID: string, userID: string, directAssign: boolean=false) {
        const url = `${Config.BACKEND.BASEURL}${Config.BACKEND.PATHS.OFFER}`;
        const headers = buildAxiosHeader();
        if (userID === "") {
            console.error(`Not able to make an offer for car ${carID}: invalid user id`);
            return [];
        }
        try {
            const cars = await Axios.post(url, {carID, commercialID: myID, userID, value: offerValue, gifted: directAssign}, headers);
            return cars.data;
        } catch (e) {
            console.error(`Not able to make an offer for car ${carID}: ${JSON.stringify(e)}`);
        }
        return [];
    }

    static async retrievePicturesForCar(carID: string) {
        const url = `${Config.BACKEND.BASEURL}${Config.BACKEND.PATHS.CARS}/${carID}/retrieve_pictures`;
        const headers = buildAxiosHeader();
        try {
            const cars = await Axios.get(url, headers);
            return cars.data;
        } catch (e: any) {
            console.error(`Not able to retrieve pictures for car ${carID}: ${JSON.stringify(e.message)}`);
            throw e;
        }
    }

    static async getCarBySearch(search: string, sellID?: string) {
        var myUrl = `${Config.BACKEND.BASEURL}${Config.BACKEND.PATHS.CARS}/search/${search}`;
        if (sellID)
            myUrl = `${Config.BACKEND.BASEURL}${Config.BACKEND.PATHS.CARS}/search/${search}/${sellID}`;
        try {
            const myRet = await Axios.get(myUrl, buildAxiosHeader());
            if (myRet.data)
                return myRet.data.map((v: any) => {return {label: `${v.Car.RegNb} - ${v.Car.Brand} - ${v.Car.Model}`.toUpperCase(), result: v}});
            else
                return [];
        } catch (error) {
            return [];
        }
    }

    static async getCarInfo() {
        const myUrl = `${Config.BACKEND.BASEURL}${Config.BACKEND.PATHS.CARSINFO}`;
        const carInfo = await Axios.get(myUrl, buildAxiosHeader());
        return carInfo.data;
    }

    static async addNewCars(file: FileList, carData: TemplateCarDataType, loaRef?: PriceListRefType, lldRef?: PriceListRefType, EquipmentsToCreate?: EquipmentsToUpdate[]) {
        const url = `${Config.BACKEND.BASEURL}${Config.BACKEND.PATHS.CARS}`;
        const formData = new FormData();
        formData.append("car", JSON.stringify(carData));
        if (EquipmentsToCreate)
            formData.append("equipment", JSON.stringify(EquipmentsToCreate));
        if (loaRef)
            formData.append("loaRef", JSON.stringify(loaRef));
        if (lldRef)
            formData.append("lldRef", JSON.stringify(lldRef));

        for (let i = 0; i < file.length; ++i)
            formData.append(`pictures`, file[i]);

        return Axios.post(url, formData, buildAxiosHeaderFile());
    }

    static async getCarList(params: generalQueryParams) {
        try {
            const builtUrl = buildURL(params, `${Config.BACKEND.BASEURL}${Config.BACKEND.PATHS.CARS}`);
            const carList = await Axios.get(builtUrl, buildAxiosHeader());
            return carList.data;
        } catch (error) {
            throw error;
        }
    }

    static getCarPictures(id: string, index?: number) {
        let url = `${Config.BACKEND.BASEURL}${Config.BACKEND.PATHS.GET_CAR_PICTURE}/${id}`;
        if (typeof(index) == "number")
            url = `${Config.BACKEND.BASEURL}${Config.BACKEND.PATHS.GET_CAR_PICTURE}/${id}/${index}`;
        return Axios.get(url, buildAxiosHeader());

    }

    static async getCarEquipements(id: string) {
        try {
            const carEquipment = await Axios.get(`${Config.BACKEND.BASEURL}${Config.BACKEND.PATHS.GET_CAR_EQUIPMENT}/${id}`, buildAxiosHeader());
            return carEquipment.data;
        } catch (error) {
            return [];
        }
    }

    static templateData() {
        return Axios.get(`${Config.BACKEND.BASEURL}${Config.BACKEND.PATHS.CARS}/template_data`, buildAxiosHeader());
    }
}
